import axios from "axios";
import { getIdToken, signOut } from "firebase/auth";
import { auth } from "../../../modules/auth/infrastructure/firebase";

// Pages that open a Firebase session before its MFA challenge is passed.
const MFA_PENDING_PATHS = ["/login", "/signup", "/verify-email", "/auth/action"];

const apiClient = axios.create({
  baseURL: "/api",
  headers: {
    "Content-Type": "application/json",
  },
  paramsSerializer: {
    indexes: null,
  },
});

apiClient.interceptors.request.use(async (config) => {
  const user = auth.currentUser;
  if (user) {
    const token = await getIdToken(user, false);
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // A session that never passed the second factor (one opened before MFA
    // was bound to the sign-in, or an abandoned challenge) cannot use the
    // API: start the sign-in again. Not on the auth pages, which hold such a
    // session on purpose while the challenge is on screen.
    if (
      error.response?.status === 403 &&
      error.response?.data?.detail === "mfa_required" &&
      !MFA_PENDING_PATHS.some((path) => window.location.pathname.startsWith(path))
    ) {
      await signOut(auth).catch(() => undefined);
      window.location.assign("/login");
      return Promise.reject(error);
    }

    if (error.response?.status !== 401 || originalRequest._retry) {
      return Promise.reject(error);
    }

    originalRequest._retry = true;

    try {
      const token = await auth.currentUser?.getIdToken(true);
      originalRequest.headers["Authorization"] = `Bearer ${token}`;
      return apiClient(originalRequest);
    } catch (err) {
      return Promise.reject(err);
    }
  }
);

export default apiClient;
